import { makeAutoObservable, reaction, runInAction } from 'mobx';
import type RootStore from './RootStore';

// Device-specific settings (localStorage)
interface IDeviceSettings {
  theme: 'light' | 'dark' | 'auto';
  language: string;
  fontSize: 'small' | 'medium' | 'large';
  sidebarCollapsed: boolean;
  notifications: {
    enabled: boolean;
    sound: boolean;
    desktop: boolean;
  };
  dashboard: {
    layout: 'grid' | 'list';
    refreshInterval: number;
    autoRefresh: boolean;
  };
  table: {
    pageSize: number;
    density: 'compact' | 'standard' | 'comfortable';
  };
}

// User-specific settings (server-persisted, SQL-compatible)
interface IUserSettings {
  UserId: number;
  TimeZone: string;
  DateFormat: string;
  TimeFormat: '12h' | '24h';
  WeekStartsOn: 0 | 1 | 6; // 0=Sunday, 1=Monday, 6=Saturday
  DefaultCurrency: string;
  EmailNotifications: boolean;
  SmsNotifications: boolean;
  MarketingEmails: boolean;
  TwoFactorEnabled: boolean;
  PrivacySettings: {
    ProfileVisibility: 'public' | 'private' | 'team';
    ShowOnlineStatus: boolean;
    ShareActivityStatus: boolean;
  };
  WorkflowPreferences: {
    AutoAssignTasks: boolean;
    TaskReminderInterval: number;
    DefaultProjectVisibility: 'public' | 'private' | 'team';
  };
  IntegrationSettings: {
    SlackNotifications: boolean;
    TeamsNotifications: boolean;
    EmailDigestFrequency: 'daily' | 'weekly' | 'monthly' | 'never';
  };
  CreatedAt?: Date;
  UpdatedAt?: Date;
}

// Default settings
const DEFAULT_DEVICE_SETTINGS: IDeviceSettings = {
  theme: 'light',
  language: 'en',
  fontSize: 'medium',
  sidebarCollapsed: false,
  notifications: {
    enabled: true,
    sound: true,
    desktop: true,
  },
  dashboard: {
    layout: 'grid',
    refreshInterval: 30000,
    autoRefresh: true,
  },
  table: {
    pageSize: 25,
    density: 'standard',
  },
};

const DEFAULT_USER_SETTINGS: Partial<IUserSettings> = {
  TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  DateFormat: 'MM/dd/yyyy',
  TimeFormat: '12h',
  WeekStartsOn: 0,
  DefaultCurrency: 'USD',
  EmailNotifications: true,
  SmsNotifications: false,
  MarketingEmails: false,
  TwoFactorEnabled: false,
  PrivacySettings: {
    ProfileVisibility: 'team',
    ShowOnlineStatus: true,
    ShareActivityStatus: true,
  },
  WorkflowPreferences: {
    AutoAssignTasks: false,
    TaskReminderInterval: 24,
    DefaultProjectVisibility: 'team',
  },
  IntegrationSettings: {
    SlackNotifications: false,
    TeamsNotifications: false,
    EmailDigestFrequency: 'weekly',
  },
};

class DeviceSettingsStore {
  settings: IDeviceSettings;
  rootStore: RootStore;
  private storageKey = 'intrasync-device-settings';

  constructor(rootStore: RootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
    this.settings = this.loadFromStorage();
  }

  private loadFromStorage(): IDeviceSettings {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...DEFAULT_DEVICE_SETTINGS, ...parsed };
      }
    } catch (error) {
      console.warn('Failed to load device settings from localStorage:', error);
    }
    return { ...DEFAULT_DEVICE_SETTINGS };
  }

  private saveToStorage() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.settings));
    } catch (error) {
      console.error('Failed to save device settings to localStorage:', error);
    }
  }

  updateSettings(updates: Partial<IDeviceSettings>) {
    runInAction(() => {
      this.settings = { ...this.settings, ...updates };
      this.saveToStorage();
    });
  }

  setTheme(theme: IDeviceSettings['theme']) {
    this.updateSettings({ theme });
  }

  setLanguage(language: string) {
    this.updateSettings({ language });
  }

  setFontSize(fontSize: IDeviceSettings['fontSize']) {
    this.updateSettings({ fontSize });
  }

  toggleSidebar() {
    this.updateSettings({ sidebarCollapsed: !this.settings.sidebarCollapsed });
  }

  updateNotifications(notifications: Partial<IDeviceSettings['notifications']>) {
    this.updateSettings({
      notifications: { ...this.settings.notifications, ...notifications }
    });
  }

  updateDashboard(dashboard: Partial<IDeviceSettings['dashboard']>) {
    this.updateSettings({
      dashboard: { ...this.settings.dashboard, ...dashboard }
    });
  }

  updateTable(table: Partial<IDeviceSettings['table']>) {
    this.updateSettings({
      table: { ...this.settings.table, ...table }
    });
  }

  resetToDefaults() {
    runInAction(() => {
      this.settings = { ...DEFAULT_DEVICE_SETTINGS };
      this.saveToStorage();
    });
  }

  // Computed getters
  get theme() { return this.settings.theme; }
  get language() { return this.settings.language; }
  get fontSize() { return this.settings.fontSize; }
  get sidebarCollapsed() { return this.settings.sidebarCollapsed; }
  get notifications() { return this.settings.notifications; }
  get dashboard() { return this.settings.dashboard; }
  get table() { return this.settings.table; }
}

class UserSettingsStore {
  rootStore: RootStore;
  settings: IUserSettings;
  loading = false;
  error: string | null = null;
  private storageKey = 'intrasync-user-settings';

  constructor(rootStore: RootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
    this.settings = this.loadFromStorage();

    // Load user settings when user changes
    reaction(
      () => this.currentUser?.id,
      (userId) => {
        if (userId) {
          this.loadUserSettings(userId);
        }
      },
      { fireImmediately: true }
    );
  }

  private loadFromStorage(): IUserSettings {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...DEFAULT_USER_SETTINGS, ...parsed } as IUserSettings;
      }
    } catch (error) {
      console.warn('Failed to load user settings from localStorage:', error);
    }
    return { ...DEFAULT_USER_SETTINGS, UserId: 0 } as IUserSettings;
  }

  private saveToStorage() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.settings));
    } catch (error) {
      console.error('Failed to save user settings to localStorage:', error);
    }
  }

  private loadUserSettings(userId: number) {
    runInAction(() => {
      // Update the UserId in current settings when user changes
      this.settings = { ...this.settings, UserId: userId };
      this.saveToStorage();
    });
  }

  updateSettings(updates: Partial<IUserSettings>) {
    runInAction(() => {
      this.settings = {
        ...this.settings,
        ...updates,
        UpdatedAt: new Date()
      };
      this.saveToStorage();
    });
  }

  // Convenience methods for common updates
  setTimeZone(timeZone: string) {
    this.updateSettings({ TimeZone: timeZone });
  }

  setDateFormat(dateFormat: string) {
    this.updateSettings({ DateFormat: dateFormat });
  }

  setTimeFormat(timeFormat: '12h' | '24h') {
    this.updateSettings({ TimeFormat: timeFormat });
  }

  setCurrency(currency: string) {
    this.updateSettings({ DefaultCurrency: currency });
  }

  updateNotificationSettings(settings: Partial<Pick<IUserSettings, 'EmailNotifications' | 'SmsNotifications' | 'MarketingEmails'>>) {
    this.updateSettings(settings);
  }

  updatePrivacySettings(privacy: Partial<IUserSettings['PrivacySettings']>) {
    this.updateSettings({
      PrivacySettings: { ...this.settings.PrivacySettings, ...privacy }
    });
  }

  updateWorkflowPreferences(workflow: Partial<IUserSettings['WorkflowPreferences']>) {
    this.updateSettings({
      WorkflowPreferences: { ...this.settings.WorkflowPreferences, ...workflow }
    });
  }

  updateIntegrationSettings(integration: Partial<IUserSettings['IntegrationSettings']>) {
    this.updateSettings({
      IntegrationSettings: { ...this.settings.IntegrationSettings, ...integration }
    });
  }

  resetToDefaults() {
    runInAction(() => {
      this.settings = {
        ...DEFAULT_USER_SETTINGS,
        UserId: this.settings.UserId,
        CreatedAt: this.settings.CreatedAt
      } as IUserSettings;
      this.saveToStorage();
    });
  }

  // Computed getters
  get currentUser() {
    return this.rootStore.authStore?.currentUser;
  }

  get isLoaded() {
    return this.settings !== null;
  }

  get hasError() {
    return this.error !== null;
  }

  // User info getters (from AuthStore)
  get currentFullName() {
    if (!this.currentUser) return '';
    return `${this.currentUser.firstName} ${this.currentUser.lastName}`;
  }

  get currentFirstName() {
    return this.currentUser?.firstName || '';
  }

  get currentLastName() {
    return this.currentUser?.lastName || '';
  }

  get currentEmail() {
    return this.currentUser?.email || '';
  }

  // Settings getters with null-safe fallbacks
  get timeZone() { return this.settings?.TimeZone || DEFAULT_USER_SETTINGS.TimeZone!; }
  get dateFormat() { return this.settings?.DateFormat || DEFAULT_USER_SETTINGS.DateFormat!; }
  get timeFormat() { return this.settings?.TimeFormat || DEFAULT_USER_SETTINGS.TimeFormat!; }
  get defaultCurrency() { return this.settings?.DefaultCurrency || DEFAULT_USER_SETTINGS.DefaultCurrency!; }
  get emailNotifications() { return this.settings?.EmailNotifications ?? DEFAULT_USER_SETTINGS.EmailNotifications!; }
  get smsNotifications() { return this.settings?.SmsNotifications ?? DEFAULT_USER_SETTINGS.SmsNotifications!; }
  get privacySettings() { return this.settings?.PrivacySettings || DEFAULT_USER_SETTINGS.PrivacySettings!; }
  get workflowPreferences() { return this.settings?.WorkflowPreferences || DEFAULT_USER_SETTINGS.WorkflowPreferences!; }
  get integrationSettings() { return this.settings?.IntegrationSettings || DEFAULT_USER_SETTINGS.IntegrationSettings!; }
}

class SettingsStore {
  deviceSettings: DeviceSettingsStore;
  userSettings: UserSettingsStore;
  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
    this.deviceSettings = new DeviceSettingsStore(rootStore);
    this.userSettings = new UserSettingsStore(rootStore);

    // Sync device theme settings with ThemeStore
    reaction(
      () => this.deviceSettings.theme,
      (theme) => {
        // Map SettingsStore theme values to ThemeStore theme values
        const themeMode = theme === 'auto' ? 'auto' : theme;
        this.rootStore.themeStore?.setThemeMode(themeMode);
      },
      { fireImmediately: true }
    );
  }

  // Legacy compatibility methods
  get appSettings() {
    return this.deviceSettings;
  }

  // Convenience getters for common settings
  get theme() { return this.deviceSettings.theme; }
  get language() { return this.deviceSettings.language; }
  get timeZone() { return this.userSettings.timeZone; }
  get dateFormat() { return this.userSettings.dateFormat; }
  get timeFormat() { return this.userSettings.timeFormat; }

  // Combined loading state
  get isLoading() {
    return this.userSettings.loading;
  }

  get hasError() {
    return this.userSettings.hasError;
  }

  get error() {
    return this.userSettings.error;
  }

  // Reset all settings
  resetAllSettings() {
    this.deviceSettings.resetToDefaults();
    this.userSettings.resetToDefaults();
  }
}

export default SettingsStore;
